import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, BellOff, Loader2, Check, Mail } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function PriceAlertBox({ product }) {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | subscribed | error
  const [isSubscribed, setIsSubscribed] = useState(false);

  const lang = i18n.language || 'en';

  const labels = {
    it: { title: 'Avviso Prezzo', desc: 'Ricevi un\'email quando il prezzo scende', btn: 'Avvisami', subscribed: 'Avviso attivo!', placeholder: 'La tua email', unsub: 'Disattiva avviso' },
    en: { title: 'Price Alert', desc: 'Get notified when the price drops', btn: 'Notify me', subscribed: 'Alert active!', placeholder: 'Your email', unsub: 'Remove alert' },
    de: { title: 'Preisalarm', desc: 'Erhalten Sie eine E-Mail bei Preissenkung', btn: 'Benachrichtigen', subscribed: 'Alarm aktiv!', placeholder: 'Ihre E-Mail', unsub: 'Alarm entfernen' },
    fr: { title: 'Alerte Prix', desc: 'Recevez un email quand le prix baisse', btn: 'Me notifier', subscribed: 'Alerte active!', placeholder: 'Votre email', unsub: 'Supprimer l\'alerte' },
    es: { title: 'Alerta de Precio', desc: 'Recibe un email cuando baje el precio', btn: 'Notificarme', subscribed: 'Alerta activa!', placeholder: 'Tu email', unsub: 'Quitar alerta' },
    pl: { title: 'Alert cenowy', desc: 'Otrzymaj email gdy cena spadnie', btn: 'Powiadom mnie', subscribed: 'Alert aktywny!', placeholder: 'Twój email', unsub: 'Usuń alert' },
  };
  const l = labels[lang] || labels.en;

  // Check if already subscribed (from localStorage)
  useEffect(() => {
    const savedEmail = localStorage.getItem('priceAlertEmail');
    if (savedEmail) {
      setEmail(savedEmail);
      checkSubscription(savedEmail);
    }
  }, [product?.id]);

  const checkSubscription = async (e) => {
    try {
      const res = await axios.get(`${API}/price-alerts/check`, { params: { email: e, product_id: product?.id } });
      setIsSubscribed(res.data?.subscribed || false);
      if (res.data?.subscribed) setStatus('subscribed');
    } catch { /* ignore */ }
  };

  const handleSubscribe = async (e) => {
    e?.preventDefault();
    if (!email.trim() || !email.includes('@')) return;
    setStatus('loading');
    try {
      const res = await axios.post(`${API}/price-alerts`, {
        email: email.trim(),
        product_id: product?.id,
        product_name: product?.name || '',
        current_price: parseFloat(product?.price || 0),
      });
      if (res.data?.success) {
        setStatus('subscribed');
        setIsSubscribed(true);
        localStorage.setItem('priceAlertEmail', email.trim());
      }
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 2500);
    }
  };

  const handleUnsubscribe = async () => {
    setStatus('loading');
    try {
      await axios.delete(`${API}/price-alerts/${product?.id}`, { params: { email: email.trim() } });
      setIsSubscribed(false);
      setStatus('idle');
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('subscribed'), 2000);
    }
  };

  if (!product?.id) return null;

  return (
    <div className="mt-5 bg-gradient-to-r from-amber-500/5 to-teal-500/5 border border-white/10 rounded-2xl p-4" data-testid="price-alert-box">
      <div className="flex items-center gap-2 mb-2">
        <Bell className="w-4 h-4 text-amber-400" />
        <span className="text-white font-semibold text-sm">{l.title}</span>
      </div>
      <p className="text-slate-400 text-xs mb-3">{l.desc}</p>

      {status === 'subscribed' ? (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-teal-500/20 rounded-full flex items-center justify-center">
              <Check className="w-3.5 h-3.5 text-teal-400" />
            </div>
            <span className="text-teal-400 text-sm font-medium">{l.subscribed}</span>
            <span className="text-slate-500 text-xs">{email}</span>
          </div>
          <button
            onClick={handleUnsubscribe}
            className="text-slate-500 hover:text-rose-400 text-xs transition-colors flex items-center gap-1"
            data-testid="price-alert-unsubscribe"
          >
            <BellOff className="w-3 h-3" />
            {l.unsub}
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubscribe} className="flex gap-2">
          <div className="flex-1 relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={l.placeholder}
              className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:border-amber-500/50 focus:outline-none transition-colors"
              required
              data-testid="price-alert-email"
            />
          </div>
          <button
            type="submit"
            disabled={status === 'loading' || !email.includes('@')}
            className="bg-amber-500/90 hover:bg-amber-400 disabled:bg-slate-700 disabled:cursor-not-allowed text-black font-semibold px-5 py-2.5 rounded-xl text-sm transition-all flex items-center gap-1.5 whitespace-nowrap"
            data-testid="price-alert-subscribe"
          >
            {status === 'loading' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Bell className="w-3.5 h-3.5" />
                {l.btn}
              </>
            )}
          </button>
        </form>
      )}

      {status === 'error' && (
        <p className="text-rose-400 text-xs mt-2">Errore. Riprova.</p>
      )}
    </div>
  );
}
